/**
 * TanStack Query hooks for the admin audit-log viewer (STORY-008-008).
 *   useAuditLog  — useInfiniteQuery over the keyset-paginated read endpoint.
 *   useAuditRow  — useQuery for the drawer detail view.
 * No mutations: the audit surface is READ-only.
 */
import {
  useInfiniteQuery,
  useQuery,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  auditKeys,
  getAuditRow,
  listAuditLog,
  type AuditFilters,
  type AuditLogItem,
  type AuditLogListResponse,
} from "@/features/admin/audit/api";

export const AUDIT_PAGE_LIMIT = 50;

export function useAuditLog(
  filters: AuditFilters,
): UseInfiniteQueryResult<InfiniteData<AuditLogListResponse>, Error> {
  return useInfiniteQuery<
    AuditLogListResponse,
    Error,
    InfiniteData<AuditLogListResponse>,
    ReturnType<typeof auditKeys.list>,
    string | null
  >({
    queryKey: auditKeys.list(filters),
    queryFn: ({ pageParam }) =>
      listAuditLog({ ...filters, cursor: pageParam, limit: AUDIT_PAGE_LIMIT }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? null,
    staleTime: 10_000,
  });
}

export function useAuditRow(
  id: string | null,
): UseQueryResult<AuditLogItem, Error> {
  return useQuery<AuditLogItem, Error>({
    queryKey: auditKeys.detail(id ?? ""),
    queryFn: () => getAuditRow(id!),
    enabled: id !== null,
    staleTime: 0,
  });
}
